import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Copy, Download, Upload, AlertTriangle, Trash2, Undo2 } from "lucide-react";
import { API, errMsg, TOPIC_NAMES } from "../../lib/api";
import { DifficultyBadge } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";

const STATUSES = ["draft", "ai_review", "clinical_review", "approved", "published", "retired"];
const STATUS_STYLES = {
  draft: "bg-muted text-muted-foreground",
  ai_review: "bg-accent/30 text-primary",
  clinical_review: "bg-accent/30 text-primary",
  approved: "bg-success/10 text-success",
  published: "bg-success text-success-foreground",
  retired: "bg-destructive/10 text-destructive",
};
const NEXT_ACTIONS = {
  draft: [{ to: "clinical_review", label: "Submit for review" }],
  ai_review: [{ to: "clinical_review", label: "Pass AI review" }, { to: "draft", label: "Reject" }],
  clinical_review: [{ to: "approved", label: "Approve" }, { to: "draft", label: "Reject" }],
  approved: [{ to: "published", label: "Publish" }, { to: "draft", label: "Reject" }],
  published: [{ to: "retired", label: "Retire" }],
  retired: [{ to: "draft", label: "Restore to draft" }],
};

export default function AdminQuestions() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ status: "all", topic: "all", difficulty: "all", q: "" });
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [publishAllOpen, setPublishAllOpen] = useState(false);
  const [publishingAll, setPublishingAll] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null); // question object or "bulk"
  const [deleting, setDeleting] = useState(false);

  const load = async (p = page) => {
    setSelected(new Set());
    const params = new URLSearchParams({ page: p, page_size: 20 });
    if (filters.status !== "all") params.set("status", filters.status);
    if (filters.topic !== "all") params.set("topic", filters.topic);
    if (filters.difficulty !== "all") params.set("difficulty", filters.difficulty);
    if (filters.q) params.set("q", filters.q);
    try {
      const { data } = await API.get(`/admin/questions?${params}`);
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => { load(1); setPage(1); }, [filters.status, filters.topic, filters.difficulty]);

  const changeStatus = async (q, status) => {
    try {
      await API.post(`/admin/questions/${q._id}/status`, { status });
      toast.success(`${q.qid} → ${status}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const duplicate = async (q) => {
    try {
      const { data } = await API.post(`/admin/questions/${q._id}/duplicate`);
      toast.success(`Duplicated as ${data.qid} (draft)`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const publishAll = async () => {
    setPublishingAll(true);
    try {
      const { data } = await API.post("/admin/questions/publish-all");
      toast.success(`Published ${data.published} questions`);
      setPublishAllOpen(false);
      load(1);
    } catch (e) { toast.error(errMsg(e)); } finally { setPublishingAll(false); }
  };

  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const doDelete = async () => {
    setDeleting(true);
    try {
      if (deleteTarget === "bulk") {
        const { data: res } = await API.post("/admin/questions/delete-bulk", { ids: [...selected] });
        toast.success(`Deleted ${res.deleted} questions — use Undo delete to restore`);
        setSelected(new Set());
      } else {
        await API.delete(`/admin/questions/${deleteTarget._id}`);
        toast.success(`Deleted ${deleteTarget.qid} — use Undo delete to restore`);
      }
      setDeleteTarget(null);
      load(1);
    } catch (e) { toast.error(errMsg(e)); } finally { setDeleting(false); }
  };

  const undoDelete = async () => {
    try {
      const { data: res } = await API.post("/admin/questions/undo-delete");
      toast.success(`Restored ${res.restored} question${res.restored === 1 ? "" : "s"}`);
      load(1);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doImport = async () => {
    setImporting(true);
    try {
      const parsed = JSON.parse(importText);
      const questions = Array.isArray(parsed) ? parsed : [parsed];
      const { data } = await API.post("/admin/questions/import", { questions });
      toast.success(`Imported ${data.created.length} questions as drafts${data.errors.length ? ` (${data.errors.length} errors)` : ""}`);
      if (data.errors.length) console.warn(data.errors);
      setImportOpen(false);
      setImportText("");
      load(1);
    } catch (e) { toast.error(errMsg(e, "Invalid JSON")); } finally { setImporting(false); }
  };

  return (
    <div data-testid="admin-questions-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-display text-2xl font-bold" data-testid="admin-questions-title">Questions</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="export-btn"
            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/questions/export`, "_blank")}>
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" data-testid="import-btn" onClick={() => setImportOpen(true)}>
            <Upload className="w-4 h-4 mr-1.5" /> Import JSON
          </Button>
          <Button size="sm" data-testid="new-question-btn" onClick={() => navigate("/admin/questions/new")}>
            <Plus className="w-4 h-4 mr-1.5" /> New question
          </Button>
          <Button size="sm" variant="secondary" data-testid="publish-all-btn" onClick={() => setPublishAllOpen(true)}>
            Publish all
          </Button>
          <Button size="sm" variant="outline" data-testid="undo-delete-btn" onClick={undoDelete}>
            <Undo2 className="w-4 h-4 mr-1.5" /> Undo delete
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="w-44" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`filter-status-${s}`}>{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.topic} onValueChange={(v) => setFilters({ ...filters, topic: v })}>
          <SelectTrigger className="w-52" data-testid="filter-topic"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All topics</SelectItem>
            {Object.entries(TOPIC_NAMES).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`filter-topic-${k}`}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.difficulty} onValueChange={(v) => setFilters({ ...filters, difficulty: v })}>
          <SelectTrigger className="w-36" data-testid="filter-difficulty"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All levels</SelectItem>
            {["easy", "medium", "hard"].map((d) => <SelectItem key={d} value={d} data-testid={`filter-difficulty-${d}`}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Search stem, ID, condition…" value={filters.q} data-testid="filter-search"
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && load(1)} className="w-64" />
        <Button variant="outline" data-testid="filter-search-btn" onClick={() => load(1)}>Search</Button>
      </div>

      {selected.size > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 bg-secondary border border-border rounded-lg px-4 py-3" data-testid="bulk-bar">
          <span className="text-sm font-medium" data-testid="bulk-selected-count">{selected.size} selected</span>
          <Button size="sm" variant="destructive" data-testid="bulk-delete-btn" onClick={() => setDeleteTarget("bulk")}>
            <Trash2 className="w-4 h-4 mr-1.5" /> Delete selected
          </Button>
          <Button size="sm" variant="ghost" data-testid="bulk-clear-btn" onClick={() => setSelected(new Set())}>Clear selection</Button>
        </div>
      )}

      {!data ? <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p> : (
        <>
          <p className="text-xs text-muted-foreground mb-3" data-testid="questions-total">{data.total} questions</p>
          <div className="bg-card border border-border rounded-lg divide-y divide-border" data-testid="admin-questions-list">
            {data.questions.map((q) => (
              <div key={q._id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`admin-question-row-${q.qid}`}>
                <Checkbox data-testid={`select-${q.qid}`} checked={selected.has(q._id)}
                  onCheckedChange={() => toggleSelect(q._id)} aria-label={`Select ${q.qid}`} />
                <div className="flex-1 min-w-[280px]">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-primary">{q.qid}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[q.status]}`} data-testid={`status-badge-${q.qid}`}>
                      {q.status.replace("_", " ")}
                    </span>
                    <DifficultyBadge level={q.difficulty} />
                    {q.has_open_report && (
                      <span className="flex items-center gap-1 text-xs font-semibold text-destructive" data-testid={`report-badge-${q.qid}`}>
                        <AlertTriangle className="w-3.5 h-3.5" /> Reported
                      </span>
                    )}
                  </div>
                  <p className="text-sm line-clamp-1">{q.stem}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{TOPIC_NAMES[q.topic]} · {q.subtopic}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" data-testid={`edit-${q.qid}`} onClick={() => navigate(`/admin/questions/${q._id}`)}>Edit</Button>
                  <Button variant="ghost" size="sm" data-testid={`duplicate-${q.qid}`} onClick={() => duplicate(q)} title="Duplicate">
                    <Copy className="w-4 h-4" />
                  </Button>
                  {(NEXT_ACTIONS[q.status] || []).map((a) => (
                    <Button key={a.to} size="sm" variant={a.to === "published" || a.to === "approved" ? "default" : "outline"}
                      data-testid={`action-${a.to}-${q.qid}`} onClick={() => changeStatus(q, a.to)}>
                      {a.label}
                    </Button>
                  ))}
                  <Button variant="ghost" size="sm" data-testid={`delete-${q.qid}`} onClick={() => setDeleteTarget(q)} title="Delete question">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
            {data.questions.length === 0 && <p className="p-10 text-center text-sm text-muted-foreground">No questions match these filters.</p>}
          </div>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" size="sm" data-testid="prev-page-btn" disabled={page <= 1} onClick={() => { setPage(page - 1); load(page - 1); }}>Previous</Button>
            <span className="text-sm text-muted-foreground self-center">Page {data.page} of {Math.max(1, Math.ceil(data.total / data.page_size))}</span>
            <Button variant="outline" size="sm" data-testid="next-page-btn" disabled={page * data.page_size >= data.total} onClick={() => { setPage(page + 1); load(page + 1); }}>Next</Button>
          </div>
        </>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent data-testid="delete-question-dialog">
          <DialogHeader>
            <DialogTitle>{deleteTarget === "bulk" ? `Delete ${selected.size} selected question${selected.size === 1 ? "" : "s"}?` : `Delete ${deleteTarget?.qid}?`}</DialogTitle>
            <DialogDescription>
              Deleted questions are moved to trash and removed from the bank and user stats immediately. You can restore the most recent deletion with the Undo delete button.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="destructive" data-testid="confirm-delete-question-btn" onClick={doDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete"}
            </Button>
            <Button variant="outline" data-testid="cancel-delete-question-btn" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={publishAllOpen} onOpenChange={setPublishAllOpen}>
        <DialogContent data-testid="publish-all-dialog">
          <DialogHeader>
            <DialogTitle>Publish all questions?</DialogTitle>
            <DialogDescription>
              This immediately publishes every unpublished question (drafts, questions in review and approved questions), skipping the review and approval steps. Only do this if you are happy for all of them to go live.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button data-testid="publish-all-confirm-btn" onClick={publishAll} disabled={publishingAll}>
              {publishingAll ? "Publishing…" : "Yes, publish everything"}
            </Button>
            <Button variant="outline" data-testid="publish-all-cancel-btn" onClick={() => setPublishAllOpen(false)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle>Import questions (JSON)</DialogTitle>
            <DialogDescription>
              Paste a JSON array of question objects with fields: topic, subtopic, difficulty, question_type, stem, options (A-E), correct_answer, explanation_correct, option_explanations, key_learning_point, reference, learning_objective, condition, tags. Imported questions are created as drafts.
            </DialogDescription>
          </DialogHeader>
          <Textarea data-testid="import-textarea" rows={12} value={importText} onChange={(e) => setImportText(e.target.value)}
            placeholder='[{"topic":"cardiology","stem":"...","options":{"A":"...","B":"...","C":"...","D":"...","E":"..."},"correct_answer":"A",...}]' />
          <Button data-testid="import-submit-btn" onClick={doImport} disabled={importing || !importText.trim()}>
            {importing ? "Importing…" : "Import as drafts"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
